'use client'

import { ChevronDown, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useId, useState } from 'react'
import { useAuth } from '@/hooks/use-auth'
import { cn } from '@/lib/utils'
import {
	badgeCount,
	badgeLabel,
	findActiveGroupId,
	groupBadge,
	isNavGroup,
	NavBadge,
	type NavBadgeCounts,
	NavBadgeDot,
	type NavGroup,
	type NavItem,
	useNavBadgeCounts,
	useNavEntries,
	useNavGroupState,
} from './nav-config'

const COLLAPSE_KEY = 'dojo.sidebar.collapsed'

/**
 * How a link renders. `rail` is the icon-only form the 14px collapsed sidebar
 * uses for its top-level entries; `full` is icon + label, used everywhere else
 * — expanded sidebar, accordion children, and the rail's flyout, all of which
 * have room for text even when the sidebar itself is collapsed.
 */
type LinkVariant = 'rail' | 'full'

export function Sidebar() {
	const pathname = usePathname() ?? '/'
	const { isAuthenticated } = useAuth()

	// Start expanded (matches SSR), then hydrate the persisted preference so we
	// don't trip a hydration mismatch on first paint.
	const [collapsed, setCollapsed] = useState(false)
	useEffect(() => {
		setCollapsed(localStorage.getItem(COLLAPSE_KEY) === '1')
	}, [])

	const toggle = () => {
		setCollapsed((c) => {
			const next = !c
			localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0')
			return next
		})
	}

	const counts = useNavBadgeCounts()
	const entries = useNavEntries()
	const groups = useNavGroupState(findActiveGroupId(entries, pathname))

	if (!isAuthenticated) return null

	return (
		<aside
			className={cn(
				'hidden md:flex shrink-0 flex-col border-r bg-card/30 transition-[width] duration-200',
				collapsed ? 'md:w-14' : 'md:w-60',
			)}
		>
			<div className={cn('flex p-2', collapsed ? 'justify-center' : 'justify-end')}>
				<button
					type="button"
					onClick={toggle}
					aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
					title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
					className="inline-flex h-8 w-8 items-center justify-center rounded-md text-foreground/60 hover:bg-muted hover:text-foreground"
				>
					{collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
				</button>
			</div>
			<nav className="flex-1 px-2 pb-4 space-y-1">
				{entries.map((entry) =>
					isNavGroup(entry) ? (
						<SidebarGroup
							key={entry.id}
							group={entry}
							pathname={pathname}
							counts={counts}
							collapsed={collapsed}
							open={groups.isOpen(entry.id)}
							onToggle={() => groups.toggle(entry.id)}
						/>
					) : (
						<SidebarLink
							key={entry.href}
							item={entry}
							pathname={pathname}
							counts={counts}
							variant={collapsed ? 'rail' : 'full'}
						/>
					),
				)}
			</nav>
		</aside>
	)
}

function SidebarLink({
	item,
	pathname,
	counts,
	variant,
}: {
	item: NavItem
	pathname: string
	counts: NavBadgeCounts
	variant: LinkVariant
}) {
	const active = item.match(pathname)
	const Icon = item.icon
	const count = badgeCount(item, counts)
	const label = item.badge && count > 0 ? badgeLabel(item.badge, count) : ''
	const rail = variant === 'rail'

	return (
		<Link
			href={item.href}
			title={item.label}
			className={cn(
				'relative flex items-center gap-3 rounded-md py-2 text-sm font-medium transition-colors',
				rail ? 'justify-center px-2' : 'px-3',
				active
					? 'bg-primary text-primary-foreground'
					: 'text-foreground/70 hover:bg-muted hover:text-foreground',
			)}
		>
			<Icon className="h-4 w-4 shrink-0" />
			{!rail && <span className="flex-1">{item.label}</span>}
			{count > 0 &&
				(rail ? <NavBadgeDot label={label} /> : <NavBadge count={count} label={label} active={active} />)}
		</Link>
	)
}

/**
 * A second-level section: an accordion when the sidebar is expanded, a flyout
 * off the icon rail when it is collapsed.
 *
 * The flyout opens three ways, and it needs all three. Hover covers the mouse,
 * `group-focus-within` covers Tab (the child links are in the tab order, so
 * focusing one reveals the panel containing it), and the button's own click
 * state covers touch — a tablet in portrait is past the `md` breakpoint, so it
 * gets this sidebar rather than <MobileNav>, and with neither hover nor a
 * keyboard a CSS-only flyout would leave every grouped page unreachable.
 * Because the click path is real state, `aria-expanded` is honest.
 */
function SidebarGroup({
	group,
	pathname,
	counts,
	collapsed,
	open,
	onToggle,
}: {
	group: NavGroup
	pathname: string
	counts: NavBadgeCounts
	collapsed: boolean
	open: boolean
	onToggle: () => void
}) {
	const bodyId = useId()
	const Icon = group.icon
	const hasActiveChild = group.items.some((item) => item.match(pathname))
	// Rolled up so a collapsed group never swallows the "something is waiting" cue.
	const badge = groupBadge(group, counts)
	const count = badge.count
	const label = count > 0 ? `${group.label}, ${badge.label}` : group.label

	// Tap-to-open state for the collapsed flyout. Held as the pathname it was
	// opened on rather than a bare boolean, so navigating closes it by
	// construction — tapping a link inside must not leave the panel pinned open
	// over the next page, and deriving that beats a reset effect that would fire
	// on every route change whether or not anything was open.
	const [pinnedFor, setPinnedFor] = useState<string | null>(null)
	const pinned = pinnedFor === pathname

	if (collapsed) {
		return (
			<div className="group relative">
				<button
					type="button"
					aria-haspopup="true"
					aria-expanded={pinned}
					aria-controls={bodyId}
					aria-label={label}
					title={group.label}
					onClick={() => setPinnedFor(pinned ? null : pathname)}
					onKeyDown={(e) => {
						if (e.key === 'Escape') setPinnedFor(null)
					}}
					className={cn(
						'relative flex w-full items-center justify-center rounded-md px-2 py-2 transition-colors',
						hasActiveChild
							? 'bg-primary text-primary-foreground'
							: 'text-foreground/70 hover:bg-muted hover:text-foreground',
					)}
				>
					<Icon className="h-4 w-4 shrink-0" />
					{/* Decorative: the count is already in the button's aria-label. */}
					{count > 0 && <NavBadgeDot />}
				</button>
				<div
					id={bodyId}
					className={cn(
						'absolute left-full top-0 z-30 ml-1 w-48 rounded-md border bg-popover p-1 shadow-md transition-opacity',
						pinned
							? 'pointer-events-auto opacity-100'
							: 'pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100',
					)}
				>
					{/* Decorative: the same text labels the list below, and announcing
					    both would double up on a screen reader. */}
					<p
						aria-hidden="true"
						className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
					>
						{group.label}
					</p>
					<ul aria-label={group.label} className="space-y-1">
						{group.items.map((item) => (
							<li key={item.href}>
								<SidebarLink item={item} pathname={pathname} counts={counts} variant="full" />
							</li>
						))}
					</ul>
				</div>
			</div>
		)
	}

	return (
		<div>
			<button
				type="button"
				onClick={onToggle}
				aria-expanded={open}
				aria-controls={bodyId}
				className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-semibold text-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
			>
				<Icon className="h-4 w-4 shrink-0" />
				<span className="flex-1 text-left">{group.label}</span>
				{!open && count > 0 && <NavBadge count={count} label={badge.label} />}
				<ChevronDown className={cn('h-3.5 w-3.5 shrink-0 transition-transform', !open && '-rotate-90')} />
			</button>
			{/* Kept in the DOM (toggling `hidden`) so aria-controls always resolves. */}
			<div id={bodyId} hidden={!open} className="ml-4 mt-1 space-y-1 border-l border-border/60 pl-2">
				{group.items.map((item) => (
					<SidebarLink key={item.href} item={item} pathname={pathname} counts={counts} variant="full" />
				))}
			</div>
		</div>
	)
}
